import { useTeamlessCollaborationCreateTeamMembersQuery } from '@trello/graphql/generated';
import { Checkbox } from '@trello/nachos';
import { memberId } from '@trello/session-cookie';
import { MemberAvatar } from 'app/src/components/MemberAvatar';
import { forNamespace } from 'app/common/lib/util/i18n';
import React, { useEffect, useReducer, useState, useMemo } from 'react';
import AnimateHeight from 'react-animate-height';
import styles from './TeamlessCollaborationCreateTeamMembers.less';

const format = forNamespace('teamless collaboration prompt');

// Avatar Height (20px) + Margin (16px) + Hr Height (1px)
const memberHeight = 20 + 16 + 1;
const numberOfVisibleMembers = 5;

type SelectedMembersAction =
  | {
      type: 'add' | 'remove';
      idMember: string;
    }
  | {
      type: 'clear';
    }
  | {
      type: 'all';
      idMembers: string[];
    };

const selectedMembersReducer = (
  selectedMembers: string[],
  action: SelectedMembersAction,
) => {
  switch (action.type) {
    case 'add':
      return [...selectedMembers, action.idMember];
    case 'remove':
      return selectedMembers.filter((idMember) => idMember !== action.idMember);
    case 'clear':
      return [];
    case 'all':
      return action.idMembers;
    default:
      return selectedMembers;
  }
};

interface TeamlessCollaborationCreateTeamMembersProps {
  boardId: string;
  onSelectMember: (selectedMembers: string[]) => void;
}

export const TeamlessCollaborationCreateTeamMembers: React.FC<TeamlessCollaborationCreateTeamMembersProps> = ({
  boardId,
  onSelectMember,
}) => {
  const [isShowingMembers, setShowingMembers] = useState(false);
  const toggleShowingMembers = () => setShowingMembers(!isShowingMembers);
  const [selectedMembers, updateMembers] = useReducer(
    selectedMembersReducer,
    [],
  );

  // If the selected members change fire the prop event
  useEffect(() => onSelectMember(selectedMembers), [
    onSelectMember,
    selectedMembers,
  ]);

  // Fetch the members
  const { data } = useTeamlessCollaborationCreateTeamMembersQuery({
    variables: { boardId },
  });
  // Filter out current logged in member
  // Memoize the results so that react can do a proper equality check on the variable
  const members = useMemo(
    () =>
      ((data && data.board && data.board.members) || []).filter(
        (member) => memberId !== member.id,
      ),
    [data],
  );
  // Set the selected members to all when the list of members changes
  // (Generally only going to happen on the first load of data)
  useEffect(() => {
    updateMembers({
      type: 'all',
      idMembers: members.map((member) => member.id),
    });
  }, [members]);

  return (
    <div className={styles.inviteMembers}>
      <Checkbox
        className={styles.checkbox}
        label={
          <>
            {format(['create team form', 'invite'], {
              membersCount: members.length,
            })}
          </>
        }
        onChange={(event) => {
          if (event.target.checked) {
            updateMembers({
              type: 'all',
              idMembers: members.map((member) => member.id),
            });
          } else {
            updateMembers({ type: 'clear' });
          }
        }}
        isChecked={selectedMembers.length > 0}
        isIndeterminate={
          selectedMembers.length !== members.length &&
          selectedMembers.length > 0
        }
      />
      <AnimateHeight
        className={styles.members}
        contentClassName={styles.membersScroller}
        height={
          isShowingMembers
            ? Math.min(numberOfVisibleMembers, members.length) * memberHeight
            : 0
        }
        animateOpacity
      >
        {members.map((member) => (
          <React.Fragment key={`member-${member.id}`}>
            <div className={styles.member}>
              <div className={styles.nameAndAvatar}>
                <MemberAvatar
                  idMember={member.id}
                  size={20}
                  className={styles.avatar}
                />{' '}
                <div className={styles.name}>
                  {member.nonPublic && member.nonPublic.fullName
                    ? member.nonPublic.fullName
                    : member.fullName}
                </div>
              </div>
              <Checkbox
                onChange={(event) => {
                  updateMembers({
                    type: event.target.checked ? 'add' : 'remove',
                    idMember: member.id,
                  });
                }}
                isChecked={selectedMembers.includes(member.id)}
                className={styles.memberCheckbox}
              />
            </div>
            <hr />
          </React.Fragment>
        ))}
      </AnimateHeight>
      <button onClick={toggleShowingMembers}>
        {isShowingMembers
          ? format(['create team form', 'hide members'])
          : format(['create team form', 'show members'])}
      </button>
    </div>
  );
};
